import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React from 'react';
import { BiCalendar } from 'react-icons/bi';
import * as styles from './styles';
import { ILeaseMaster } from 'src/models/dlcLease';
import { calculateLeaseExpirationDate } from './leaseExpirationDate';

interface IMasterSectionProps {
    leaseMaster: ILeaseMaster;
    onChange?: (field: string, value: unknown) => void;
}

const MasterSection: React.FC<IMasterSectionProps> = ({ leaseMaster, onChange }) => {
    const isEditable = !!onChange;

    const handleChange = (field: string, value: unknown) => {
        if (onChange) {
            onChange(`${field}`, value);
        }
    };

    const handleExpirationFieldChange = (field: keyof ILeaseMaster, value: unknown) => {
        handleChange(field, value);
        const updatedLease = { ...leaseMaster, [field]: value };
        handleChange('expDate', calculateLeaseExpirationDate(updatedLease));
    };

    const handleNumberChange = (field: string, value: string) => {
        const numValue = value === '' ? null : parseFloat(value);
        handleChange(field, numValue);
    };

    const handleExpirationNumberChange = (field: keyof ILeaseMaster, value: string) => {
        const numValue = value === '' ? null : parseFloat(value);
        handleExpirationFieldChange(field, numValue);
    };

    const handleRentalChange = (field: string, value: unknown) => {
        if (onChange) {
            onChange(`rentalInfo.${field}`, value);
        }
    };

    const handleRentalNumberChange = (field: string, value: string) => {
        const numValue = value === '' ? null : parseFloat(value);
        handleRentalChange(field, numValue);
    };

    return (
        <div style={styles.sectionContainer}>
            <h2 style={styles.sectionHeader}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="9" y1="9" x2="15" y2="9" />
                        <line x1="9" y1="15" x2="15" y2="15" />
                    </svg>
                    Master Information
                </span>
            </h2>
            {/* Row-based grid layout using all available width */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.5rem 0.75rem', alignItems: 'end' }}>
                {/* Row 1: Term | Royalty | ORRI | CD Number | Pooling Order # | Client Lease # */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.25rem' }}>
                    <Field
                        label="Term"
                        value={leaseMaster.term?.toString() || ''}
                        type="number"
                        editable={isEditable}
                        onChange={(value) => handleExpirationNumberChange('term', value)}
                    />
                    <SelectField
                        label="&nbsp;"
                        value={leaseMaster.termUnits || ''}
                        editable={isEditable}
                        onChange={(value) => handleExpirationFieldChange('termUnits', value)}
                        options={[
                            { value: 'Y', label: 'Yrs' },
                            { value: 'M', label: 'Mo' },
                            { value: 'D', label: 'Days' },
                        ]}
                    />
                </div>
                <Field label="Royalty" value={leaseMaster.royalty || ''} editable={isEditable} onChange={(value) => handleChange('royalty', value)} />
                <Field
                    label="ORRI Burdens"
                    value={leaseMaster.orri?.toString() || ''}
                    type="number"
                    editable={isEditable}
                    onChange={(value) => handleNumberChange('orri', value)}
                />
                <Field label="CD Number" value={leaseMaster.cd || ''} editable={isEditable} onChange={(value) => handleChange('cd', value)} />
                <Field
                    label="Pooling Order #"
                    value={leaseMaster.orderNo || ''}
                    editable={isEditable}
                    onChange={(value) => handleChange('orderNo', value)}
                />
                <Field
                    label="Client Lease #"
                    value={leaseMaster.clientLease || ''}
                    editable={isEditable}
                    onChange={(value) => handleChange('clientLease', value)}
                />

                {/* Row 2: Lease Date | Eff. Date | Exp. Date | Exposed Date | Bonus/Acre | Draft Note */}
                <DateField
                    label="Lease Date"
                    value={leaseMaster.leaseDate}
                    editable={isEditable}
                    onChange={(value) => handleExpirationFieldChange('leaseDate', value)}
                />
                <DateField
                    label="Eff. Date"
                    value={leaseMaster.effDate}
                    editable={isEditable}
                    onChange={(value) => handleExpirationFieldChange('effDate', value)}
                />
                <DateField label="Exp. Date" value={leaseMaster.expDate} editable={false} />
                <DateField
                    label="Exposed Date"
                    value={leaseMaster.exposedDate}
                    editable={isEditable}
                    onChange={(value) => handleChange('exposedDate', value)}
                />
                <Field
                    label="Bonus / Acre"
                    value={leaseMaster.bonusAcre?.toFixed(2) || ''}
                    type="number"
                    prefix="$"
                    editable={false}
                    onChange={(value) => handleNumberChange('bonusAcre', value)}
                />
                <Field
                    label="Draft Note"
                    value={leaseMaster.draftHoldReason}
                    editable={isEditable}
                    onChange={(value) => handleChange('draftHoldReason', value)}
                />

                {/* Row 3: Options & Flags */}
                <div style={optionRenewalContainerStyle}>
                    <div style={optionRenewalGroupStyle}>
                        <div style={optionCheckboxStackStyle}>
                            <CheckboxField
                                label="Option to Renew?"
                                checked={leaseMaster.opFl === 'Y'}
                                editable={isEditable}
                                onChange={(checked) => handleChange('opFl', checked ? 'Y' : 'N')}
                            />
                            <CheckboxField
                                label="Option Exercised?"
                                checked={leaseMaster.optionExercised === 'Y'}
                                editable={isEditable}
                                onChange={(checked) => handleExpirationFieldChange('optionExercised', checked ? 'Y' : 'N')}
                            />
                        </div>
                        <Field
                            label="Option Term"
                            value={leaseMaster.opTerm?.toString() || ''}
                            type="number"
                            editable={isEditable}
                            onChange={(value) => handleExpirationNumberChange('opTerm', value)}
                        />
                        <SelectField
                            label="&nbsp;"
                            value={leaseMaster.opTermUnits || ''}
                            editable={isEditable}
                            onChange={(value) => handleExpirationFieldChange('opTermUnits', value)}
                            options={[
                                { value: 'Y', label: 'Yrs' },
                                { value: 'M', label: 'Mo' },
                                { value: 'D', label: 'Days' },
                            ]}
                        />
                        <Field
                            label="Option / Acre"
                            value={leaseMaster.loBonus?.toString() || ''}
                            type="number"
                            prefix="$"
                            editable={isEditable}
                            onChange={(value) => handleNumberChange('loBonus', value)}
                        />
                    </div>

                    <div style={additionalOptionGroupStyle}>
                        <CheckboxField
                            label="Addl Opt Exercised?"
                            checked={leaseMaster.addlOptionExercised === 'Y'}
                            editable={isEditable}
                            onChange={(checked) => handleExpirationFieldChange('addlOptionExercised', checked ? 'Y' : 'N')}
                        />
                        <Field
                            label="Addl Opt Term"
                            value={leaseMaster.addlOptionTerm?.toString() || ''}
                            type="number"
                            editable={isEditable}
                            onChange={(value) => handleExpirationNumberChange('addlOptionTerm', value)}
                        />
                        <SelectField
                            label="&nbsp;"
                            value={leaseMaster.addlOptionUnits || ''}
                            editable={isEditable}
                            onChange={(value) => handleExpirationFieldChange('addlOptionUnits', value)}
                            options={[
                                { value: 'Y', label: 'Yrs' },
                                { value: 'M', label: 'Mo' },
                                { value: 'D', label: 'Days' },
                            ]}
                        />
                        <Field
                            label="Addl. / Acre"
                            value={leaseMaster.addlOptionAcre?.toString() || ''}
                            type="number"
                            prefix="$"
                            editable={isEditable}
                            onChange={(value) => handleNumberChange('addlOptionAcre', value)}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', paddingBottom: '0.25rem' }}>
                    <CheckboxField
                        label="Top Lease?"
                        checked={leaseMaster.tlFl === 'Y'}
                        editable={isEditable}
                        onChange={(checked) => handleChange('tlFl', checked ? 'Y' : 'N')}
                    />
                    <CheckboxField
                        label="Expired?"
                        checked={leaseMaster.expired === 'Y'}
                        editable={isEditable}
                        onChange={(checked) => handleChange('expired', checked ? 'Y' : 'N')}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '0.25rem' }}>
                    <CheckboxField
                        label="Rental Lease?"
                        checked={leaseMaster.rentalLease === 'Y'}
                        editable={isEditable}
                        onChange={(checked) => handleChange('rentalLease', checked ? 'Y' : 'N')}
                    />
                </div>
            </div>
            {leaseMaster.rentalLease === 'Y' && (
                <div style={{ marginTop: '0.75rem', borderTop: `1px solid ${styles.colors.gray200}`, paddingTop: '0.75rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.5rem 0.75rem', alignItems: 'end' }}>
                        <DateField
                            label="Rental Due Date"
                            value={leaseMaster.rentalInfo?.dueDate}
                            editable={isEditable}
                            onChange={(value) => handleRentalChange('dueDate', value)}
                        />
                        <Field
                            label="Rental Amount"
                            type="number"
                            prefix="$"
                            value={leaseMaster.rentalInfo?.perAcre?.toString() || ''}
                            editable={isEditable}
                            onChange={(value) => handleRentalNumberChange('perAcre', value)}
                        />
                        <SelectField
                            label="Per Acre/Flat"
                            value={leaseMaster.rentalInfo?.perFlag || ''}
                            editable={isEditable}
                            onChange={(value) => handleRentalChange('perFlag', value)}
                            options={[
                                { value: 'A', label: 'Per Acre' },
                                { value: 'F', label: 'Flat Rate' },
                            ]}
                        />
                        <Field
                            label="Bank Name"
                            value={leaseMaster.rentalInfo?.bankName1 || ''}
                            editable={isEditable}
                            onChange={(value) => handleRentalChange('bankName1', value)}
                        />
                        <Field
                            label="Address"
                            value={leaseMaster.rentalInfo?.addr1 || ''}
                            editable={isEditable}
                            onChange={(value) => handleRentalChange('addr1', value)}
                        />
                        <Field
                            label="Total Rent"
                            type="number"
                            prefix="$"
                            value={leaseMaster.rentalInfo?.annualRent?.toString() || ''}
                            editable={isEditable}
                            onChange={(value) => handleRentalNumberChange('annualRent', value)}
                        />
                        <Field
                            label="Addl. Address"
                            value={leaseMaster.rentalInfo?.addr2 || ''}
                            editable={isEditable}
                            onChange={(value) => handleRentalChange('addr2', value)}
                        />
                        <Field
                            label="City"
                            value={leaseMaster.rentalInfo?.city || ''}
                            editable={isEditable}
                            onChange={(value) => handleRentalChange('city', value)}
                        />
                        <Field
                            label="State"
                            value={leaseMaster.rentalInfo?.state || ''}
                            editable={isEditable}
                            onChange={(value) => handleRentalChange('state', value)}
                        />
                        <Field
                            label="Zip"
                            value={leaseMaster.rentalInfo?.zip || ''}
                            editable={isEditable}
                            onChange={(value) => handleRentalChange('zip', value)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const optionRenewalContainerStyle: React.CSSProperties = {
    gridColumn: '1 / -1',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '0.75rem 1rem',
    alignItems: 'end',
};

const optionRenewalGroupStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(96px, 1fr) 92px minmax(120px, 1fr)',
    gap: '0.35rem 0.5rem',
    alignItems: 'end',
};

const additionalOptionGroupStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(96px, 1fr) 92px minmax(120px, 1fr)',
    gap: '0.35rem 0.5rem',
    alignItems: 'end',
};

const optionCheckboxStackStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    paddingBottom: '0.35rem',
};

// ===================================================================================
// --- CHILD COMPONENTS (UNCHANGED) ---
// ===================================================================================

export const DateField = ({
    label,
    value,
    editable = false,
    onChange,
    inputStyle,
    onValidityChange,
    helpText,
    labelAction,
}: {
    label: string;
    value?: Date | string;
    editable?: boolean;
    onChange?: (value: string) => void;
    inputStyle?: React.CSSProperties;
    onValidityChange?: (isValid: boolean) => void;
    helpText?: string;
    labelAction?: React.ReactNode;
}) => {
    const formatDateForDisplay = (date?: Date | string): string => {
        if (!date) {
            return '';
        }

        if (typeof date === 'string') {
            const isoDateParts = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (isoDateParts) {
                return `${isoDateParts[2]}/${isoDateParts[3]}/${isoDateParts[1]}`;
            }

            const displayDateParts = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (displayDateParts) {
                return `${displayDateParts[1].padStart(2, '0')}/${displayDateParts[2].padStart(2, '0')}/${displayDateParts[3]}`;
            }
        }

        const dateObj = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(dateObj.getTime())) {
            return '';
        }

        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${month}/${day}/${year}`;
    };

    const formatDateForInput = (date?: Date | string): string => {
        const displayDate = formatDateForDisplay(date);
        const parts = displayDate.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        return parts ? `${parts[3]}-${parts[1]}-${parts[2]}` : '';
    };

    const formatTypedDate = (input: string): string => {
        const digits = input.replace(/\D/g, '').slice(0, 8);
        if (digits.length <= 2) {
            return digits;
        }
        if (digits.length <= 4) {
            return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    };

    const normalizeTypedDate = (input: string): string | null => {
        const parts = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!parts) {
            return null;
        }

        const month = Number(parts[1]);
        const day = Number(parts[2]);
        const year = Number(parts[3]);
        const date = new Date(year, month - 1, day);
        const isValid =
            year >= 1000 &&
            month >= 1 &&
            month <= 12 &&
            day >= 1 &&
            date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day;

        return isValid ? `${parts[3]}-${parts[1]}-${parts[2]}` : null;
    };

    const [displayValue, setDisplayValue] = React.useState(() => formatDateForDisplay(value));
    const [isInvalid, setIsInvalid] = React.useState(false);

    React.useEffect(() => {
        setDisplayValue(formatDateForDisplay(value));
        setIsInvalid(false);
    }, [value]);

    const commitDate = (nextDisplayValue: string): void => {
        if (!onChange) {
            return;
        }

        if (!nextDisplayValue) {
            onChange('');
            return;
        }

        const normalizedDate = normalizeTypedDate(nextDisplayValue);
        if (normalizedDate) {
            onChange(normalizedDate);
        }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
        event.target.style.borderColor = styles.colors.gray300;
        event.target.style.boxShadow = 'none';

        const normalizedDate = normalizeTypedDate(displayValue);
        if (normalizedDate || !displayValue) {
            setIsInvalid(false);
            onValidityChange?.(true);
            commitDate(displayValue);
            return;
        }

        setIsInvalid(true);
        onValidityChange?.(false);
    };

    return (
        <div style={styles.fieldContainer}>
            <label style={{ ...styles.fieldLabel, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                {label}
                {helpText && (
                    <span
                        title={helpText}
                        aria-label={helpText}
                        tabIndex={0}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '1rem',
                            height: '1rem',
                            border: `1px solid ${styles.colors.gray500}`,
                            borderRadius: '50%',
                            color: styles.colors.gray600,
                            fontSize: '0.7rem',
                            fontWeight: styles.typography.fontWeight.bold,
                            lineHeight: 1,
                            cursor: 'help',
                        }}
                    >
                        ?
                    </span>
                )}
                {labelAction}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                <LeaseRecordInput
                    type="text"
                    inputMode="numeric"
                    placeholder="mm/dd/yyyy"
                    value={displayValue}
                    disabled={!editable}
                    aria-invalid={isInvalid}
                    onChange={(event) => {
                        const nextDisplayValue = formatTypedDate(event.target.value);
                        setDisplayValue(nextDisplayValue);
                        setIsInvalid(false);
                        onValidityChange?.(!nextDisplayValue || normalizeTypedDate(nextDisplayValue) !== null);
                        commitDate(nextDisplayValue);
                    }}
                    autoComplete="off"
                    onFocus={(event) => {
                        if (editable) {
                            event.target.style.borderColor = styles.colors.borderFocus;
                            event.target.style.boxShadow = styles.inputFocus.boxShadow;
                        }
                    }}
                    onBlur={handleBlur}
                    style={{
                        ...(editable ? styles.inputBase : styles.inputDisabled),
                        paddingRight: editable ? '2.5rem' : '1rem',
                        ...(isInvalid ? { borderColor: styles.colors.danger, boxShadow: '0 0 0 1px rgba(239, 68, 68, 0.2)' } : {}),
                        ...inputStyle,
                    }}
                />
                {editable && (
                    <>
                        <BiCalendar
                            aria-hidden="true"
                            style={{
                                position: 'absolute',
                                right: '0.75rem',
                                width: '1rem',
                                height: '1rem',
                                color: styles.colors.gray500,
                                pointerEvents: 'none',
                            }}
                        />
                        <LeaseRecordInput
                            type="date"
                            value={formatDateForInput(value)}
                            aria-label={`Choose ${label}`}
                            onChange={(event) => {
                                const selectedDate = event.target.value;
                                setDisplayValue(formatDateForDisplay(selectedDate));
                                setIsInvalid(false);
                                onValidityChange?.(true);
                                onChange?.(selectedDate);
                            }}
                            tabIndex={-1}
                            style={{
                                position: 'absolute',
                                right: '0.25rem',
                                width: '2rem',
                                height: '100%',
                                opacity: 0,
                                cursor: 'pointer',
                            }}
                        />
                    </>
                )}
            </div>
            {isInvalid && (
                <span role="alert" style={{ color: styles.colors.danger, fontSize: styles.typography.fontSize.xs }}>
                    Enter a valid date in MM/DD/YYYY format.
                </span>
            )}
        </div>
    );
};

const formatCurrencyValue = (input: string): string => {
    if (input === '') {
        return '';
    }

    const amount = Number(input.replace(/[$,\s]/g, ''));
    if (!Number.isFinite(amount)) {
        return '';
    }

    return amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const formatCurrency = (value?: number | string | null): string => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const formattedValue = formatCurrencyValue(String(value));
    return formattedValue ? `$${formattedValue}` : '';
};

const formatCurrencyDraft = (input: string): string => {
    const cleaned = input.replace(/[^\d.-]/g, '');
    const isNegative = cleaned.startsWith('-');
    const unsigned = cleaned.replace(/-/g, '');
    const decimalIndex = unsigned.indexOf('.');
    const hasDecimal = decimalIndex >= 0;
    const wholeDigits = (hasDecimal ? unsigned.slice(0, decimalIndex) : unsigned).replace(/\D/g, '');
    const decimalDigits = hasDecimal
        ? unsigned
              .slice(decimalIndex + 1)
              .replace(/\D/g, '')
              .slice(0, 2)
        : '';
    const normalizedWhole = wholeDigits.replace(/^0+(?=\d)/, '') || (hasDecimal ? '0' : '');
    const groupedWhole = normalizedWhole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `${isNegative ? '-' : ''}${groupedWhole}${hasDecimal ? `.${decimalDigits}` : ''}`;
};

export const CurrencyField = ({
    label,
    value,
    editable = false,
    onChange,
    inputStyle,
}: {
    label: string;
    value: string;
    editable?: boolean;
    onChange?: (value: string) => void;
    inputStyle?: React.CSSProperties;
}) => {
    const [displayValue, setDisplayValue] = React.useState(() => formatCurrencyValue(value));
    const isFocusedRef = React.useRef(false);

    React.useEffect(() => {
        if (!isFocusedRef.current) {
            setDisplayValue(formatCurrencyValue(value));
        }
    }, [value]);

    const emitValue = (draft: string): void => {
        if (!onChange) {
            return;
        }

        if (!draft) {
            onChange('');
            return;
        }

        const normalized = draft.replace(/,/g, '');
        if (/^-?\d+(?:\.\d{0,2})?$/.test(normalized) && !normalized.endsWith('.')) {
            onChange(normalized);
        }
    };

    return (
        <div style={styles.fieldContainer}>
            <label style={styles.fieldLabel}>{label}</label>
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                <span
                    style={{
                        position: 'absolute',
                        left: '1rem',
                        color: styles.colors.gray500,
                        fontSize: styles.typography.fontSize.base,
                        fontWeight: styles.typography.fontWeight.medium,
                    }}
                >
                    $
                </span>
                <LeaseRecordInput
                    type="text"
                    inputMode="decimal"
                    value={displayValue}
                    disabled={!editable}
                    onChange={(event) => {
                        const nextDisplayValue = formatCurrencyDraft(event.target.value);
                        setDisplayValue(nextDisplayValue);
                        emitValue(nextDisplayValue);
                    }}
                    autoComplete="off"
                    onFocus={(event) => {
                        isFocusedRef.current = true;
                        if (editable) {
                            event.target.style.borderColor = styles.colors.borderFocus;
                            event.target.style.boxShadow = styles.inputFocus.boxShadow;
                        }
                    }}
                    onBlur={(event) => {
                        isFocusedRef.current = false;
                        event.target.style.borderColor = styles.colors.gray300;
                        event.target.style.boxShadow = 'none';

                        const normalized = displayValue.replace(/,/g, '');
                        if (!normalized || normalized === '-') {
                            setDisplayValue('');
                            onChange?.('');
                            return;
                        }

                        const amount = Number(normalized);
                        if (Number.isFinite(amount)) {
                            const committedValue = amount.toFixed(2);
                            setDisplayValue(formatCurrencyValue(committedValue));
                            onChange?.(committedValue);
                        } else {
                            setDisplayValue(formatCurrencyValue(value));
                        }
                    }}
                    style={{
                        ...(editable ? styles.inputBase : styles.inputDisabled),
                        paddingLeft: '2rem',
                        textAlign: 'right',
                        ...inputStyle,
                    }}
                />
            </div>
        </div>
    );
};
export const Field = ({
    label,
    value,
    type = 'text',
    prefix = '',
    editable = false,
    onChange,
    inputStyle,
}: {
    label: string;
    value: string;
    type?: string;
    prefix?: string;
    editable?: boolean;
    onChange?: (value: string) => void;
    inputStyle?: React.CSSProperties;
}) => {
    if (type === 'date') {
        return <DateField label={label} value={value} editable={editable} onChange={onChange} inputStyle={inputStyle} />;
    }
    if (prefix === '$') {
        return <CurrencyField label={label} value={value} editable={editable} onChange={onChange} inputStyle={inputStyle} />;
    }
    return (
        <div style={styles.fieldContainer}>
            <label style={styles.fieldLabel}>{label}</label>
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                {prefix && (
                    <span
                        style={{
                            position: 'absolute',
                            left: '1rem',
                            color: styles.colors.gray500,
                            fontSize: styles.typography.fontSize.base,
                            fontWeight: styles.typography.fontWeight.medium,
                        }}
                    >
                        {prefix}
                    </span>
                )}
                <LeaseRecordInput
                    type={type}
                    value={value}
                    disabled={!editable}
                    onChange={(e) => {
                        if (onChange) {
                            onChange(e.target.value);
                        }
                    }}
                    autoComplete="off"
                    onFocus={(e) => {
                        if (editable) {
                            e.target.style.borderColor = styles.colors.borderFocus;
                            e.target.style.boxShadow = styles.inputFocus.boxShadow;
                        }
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = styles.colors.gray300;
                        e.target.style.boxShadow = 'none';
                    }}
                    style={{
                        ...(editable ? styles.inputBase : styles.inputDisabled),
                        paddingLeft: prefix ? '2rem' : '1rem',
                        ...inputStyle,
                    }}
                />
            </div>
        </div>
    );
};
export const SelectField = ({
    label,
    value,
    options,
    editable = false,
    onChange,
}: {
    label: string;
    value: string;
    options: { value: string; label: string }[];
    editable?: boolean;
    onChange?: (value: string) => void;
}) => (
    <div style={styles.fieldContainer}>
        <label style={styles.fieldLabel}>{label}</label>
        <select
            value={value}
            disabled={!editable}
            onChange={(e) => {
                if (onChange) {
                    onChange(e.target.value);
                }
            }}
            autoComplete="off"
            onFocus={(e) => {
                if (editable) {
                    e.target.style.borderColor = styles.colors.borderFocus;
                    e.target.style.boxShadow = styles.inputFocus.boxShadow;
                }
            }}
            onBlur={(e) => {
                e.target.style.borderColor = styles.colors.gray300;
                e.target.style.boxShadow = 'none';
            }}
            style={editable ? styles.selectBase : { ...styles.selectBase, ...styles.inputDisabled }}
        >
            <option value="">Select...</option>
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
    </div>
);

export const CheckboxField = ({
    label,
    checked,
    editable = false,
    onChange,
}: {
    label: string;
    checked: boolean;
    editable?: boolean;
    onChange?: (checked: boolean) => void;
}) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        <label
            style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                cursor: editable ? 'pointer' : 'default',
            }}
        >
            <LeaseRecordInput
                type="checkbox"
                checked={checked}
                disabled={!editable}
                onChange={(e) => {
                    if (onChange) {
                        onChange(e.target.checked);
                    }
                }}
                style={
                    {
                        width: '14px',
                        height: '14px',
                        cursor: editable ? 'pointer' : 'default',
                        marginRight: '0.25rem',
                        accentColor: styles.colors.primary,
                    } as React.CSSProperties
                }
            />
            <span
                style={{
                    ...styles.fieldLabel,
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </span>
        </label>
    </div>
);

export default MasterSection;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Master.tsx